import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import UserTable from "../components/UserTable";
import { useUser } from "../../../apis/user/UserProvider";
import { getPageName, getUiInterface } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import ConfirmResetPasswordModal from "../../../components/ConfirmResetPasswordModal";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import { FaEdit } from "react-icons/fa";
import User from "../../../types/user";

const UserView: React.FC = () => {
    const { t } = useTranslation();
    const { usuarios, apiResponse, fetchUsuarios, resetaSenhaUsuario } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Flags de exibição: por padrão a listagem traz somente os usuários ativos,
    // com a conta não expirada e não bloqueada.
    const [mostrarDesativados, setMostrarDesativados] = useState(false);
    const [mostrarExpirados, setMostrarExpirados] = useState(false);
    const [mostrarBloqueados, setMostrarBloqueados] = useState(false);

    const [usuarioParaResetar, setUsuarioParaResetar] = useState<User | null>(null);

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 40,
        page: 0,
    });
    const [exportando, setExportando] = useState(false);

    // Buscar os usuários de uma vez (client-side), respeitando as flags de exibição
    useEffect(() => {
        fetchUsuarios({ mostrarDesativados, mostrarExpirados, mostrarBloqueados });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mostrarDesativados, mostrarExpirados, mostrarBloqueados]);

    // Filtrar usuários localmente pelo searchTerm
    const filteredUsers = useMemo(() => {
        if (!usuarios) return [];
        if (!searchTerm.trim()) return usuarios;

        const term = searchTerm.toLowerCase();
        return usuarios.filter(u =>
            (u.username?.toLowerCase().includes(term)) ||
            (u.fullName?.toLowerCase().includes(term)) ||
            (u.email?.toLowerCase().includes(term))
        );
    }, [usuarios, searchTerm]);

    useEffect(() => {
        if (location.state?.message) {
            setAlert({ message: location.state.message, variant: location.state.variant });

            navigate(location.pathname, { replace: true });

            const timeout = setTimeout(() => setAlert(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [location, navigate]);

    /**
     * Exporta para Excel os usuarios atualmente filtrados (mesmo conjunto que a
     * grid mostra). Como a lista inteira ja esta carregada no cliente, nao ha
     * busca adicional a fazer - so montar a planilha.
     */
    const handleExportarExcel = async () => {
        setExportando(true);

        try {
            const linhas = filteredUsers.map((row) => ({
                [t('user.fields.id')]: row.id,
                [t('user.fields.name')]: row.fullName ?? '',
                [t('user.fields.username')]: row.username ?? '',
                [t('user.fields.email')]: row.email ?? '',
                [t('user.fields.role')]: (row.secUserSecRoles ?? [])
                    .map((r: any) => r.secRole?.authority)
                    .filter(Boolean)
                    .join(', '),
                [t('user.fields.accountExpired')]: String(row.accountExpired),
                [t('user.fields.accountLocked')]: String(row.accountLocked),
                [t('user.fields.active')]: String(row.enabled),
            }));

            if (!(await exportarParaExcel(linhas, 'user'))) {
                setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
            }
        } catch (error) {
            setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
        } finally {
            setExportando(false);
        }
    };

    const handleResetPassword = useCallback(async () => {
        if (!usuarioParaResetar?.id) return;

        const usuario = usuarioParaResetar;
        setUsuarioParaResetar(null);

        try {
            const messages = await resetaSenhaUsuario(usuario.id!);
            setAlert({
                message: messages?.length
                    ? messages.join(" ")
                    : t('user.resetPassword.success', { user: usuario.username || "" }),
                variant: "success",
            });
        } catch (error: any) {
            const messages: string[] | undefined = error?.response?.data?.messages;
            setAlert({
                message: messages?.length ? messages.join(" ") : t('user.resetPassword.error'),
                variant: "danger",
            });
        }
    }, [usuarioParaResetar, resetaSenhaUsuario, t]);

    const navItems: NavBarItem[] = [
        { label: t('user.labels.home'), route: "/", iconName: "house-door" },
        { label: t('user.labels.new'), route: "/User/create", iconName: "plus-circle" },
        { label: t('user.labels.compare'), route: "/user/comparar", iconName: "columns-gap" },
        {
            label: t('user.labels.showDisabled'),
            iconName: "person-slash",
            type: "toggle",
            checked: mostrarDesativados,
            onToggle: setMostrarDesativados,
        },
        {
            label: t('user.labels.showExpired'),
            iconName: "calendar-x",
            type: "toggle",
            checked: mostrarExpirados,
            onToggle: setMostrarExpirados,
        },
        {
            label: t('user.labels.showLocked'),
            iconName: "lock",
            type: "toggle",
            checked: mostrarBloqueados,
            onToggle: setMostrarBloqueados,
        },
    ];

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: t('user.fields.id'),
            width: 90,
            sortable: true,
            filterable: true,
            renderCell: (params) => (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "black",
                    cursor: "pointer"
                }}
                    onClick={() => navigate(`/user/${params.row.id}`)}>
                        <FaEdit className="text-primary" style={{ marginRight: "6px", marginLeft: "2px" }}/>
                        {params.row.id}
                </span>
            ),
        },
        {
            field: "fullName",
            headerName: t('user.fields.name'),
            width: 300,
            sortable: true,
            filterable: true,
            renderCell: (params) => params.row?.fullName || ''
        },
        {
            field: "username",
            headerName: t('user.fields.username'),
            width: 200,
            sortable: true,
            filterable: true,
            renderCell: (params) => params.row?.username || ''
        },
        {
            field: "email",
            headerName: t('user.fields.email'),
            width: 250,
            sortable: true,
            filterable: true,
            renderCell: (params) => params.row?.email || '-'
        },
        {
            field: "role",
            headerName: t('user.fields.role'),
            width: 200,
            sortable: true,
            filterable: true,
            valueGetter: (value: any, row: any) => {
                const roles = row?.secUserSecRoles?.map((r: any) => r.secRole?.authority).filter(Boolean).join(', ');
                return roles || '';
            },
            renderCell: (params) => {
                const roles = params.row?.secUserSecRoles?.map((r: any) => r.secRole?.authority).filter(Boolean).join(', ');
                return roles || '-';
            }
        },
        {
            field: "accountExpired",
            headerName: t('user.fields.accountExpired'),
            width: 150,
            sortable: true,
            filterable: true,
            type: 'boolean',
            renderCell: (params) => String(params.row?.accountExpired)
        },
        {
            field: "accountLocked",
            headerName: t('user.fields.accountLocked'),
            width: 150,
            sortable: true,
            filterable: true,
            type: 'boolean',
            renderCell: (params) => String(params.row?.accountLocked)
        },
        {
            field: "enabled",
            headerName: t('user.fields.active'),
            width: 150,
            sortable: true,
            filterable: true,
            type: 'boolean',
            renderCell: (params) => String(params.row?.enabled)
        },
        {
            field: "resetPassword",
            headerName: t('user.fields.password'),
            width: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    variant="outline-warning"
                    size="sm"
                    title={t('user.resetPassword.title')}
                    onClick={() => setUsuarioParaResetar(params.row)}
                >
                    <i className="bi bi-key-fill me-2"></i>
                    {t('user.resetPassword.button')}
                </Button>
            ),
        },
    ];

    return (
        <Container fluid style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("user")}</h2>
            <div className="mb-3"></div>

            {/* Campo de busca */}
            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    placeholder={t('user.labels.search') || "Buscar usuário..."}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        // Reset para primeira página quando buscar
                        setPaginationModel({ ...paginationModel, page: 0 });
                    }}
                    style={{ maxWidth: '400px' }}
                />
            </div>

            <div>
                {alert && (
                    <AlertOverlay
                        message={alert.message}
                        variant={alert.variant}
                        onClose={() => setAlert(null)}
                    />
                )}
            </div>
            <div className="d-flex justify-content-end mb-2">
                <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <UserTable
                    rows={filteredUsers}
                    columns={columns}
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                    apiResponse={apiResponse}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                />
            </div>

            <ConfirmResetPasswordModal
                show={!!usuarioParaResetar}
                userName={usuarioParaResetar?.username}
                onHide={() => setUsuarioParaResetar(null)}
                onConfirm={handleResetPassword}
            />
        </Container>
    );
};

export default UserView;
